"use client";

import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Loader } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function RequestAQuote() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.2
  });
  return <section id="request-a-quote" ref={ref} className="py-16 px-4 bg-background">
      <div className="max-w-2xl mx-auto">
        <div className={`text-center mb-8 transition-all duration-[var(--duration-medium)] ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
        transitionDelay: "0ms"
      }}>
          <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground mb-3">
            Request a Quote
          </h2>
          <p className="text-muted-foreground text-lg">
            Let's make your special day come to life. Fill out the form below and we'll get back to you with a personalized quote for your wedding or event.
          </p>
        </div>

        <FormProvider className={`transition-all duration-[var(--duration-medium)] ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
        transitionDelay: "100ms"
      }} formId="request_quote_form_sound_and_lighting" sectionName="request-a-quote" defaultValues={{
        name: "",
        email: "",
        phone: "",
        event_date: "",
        guest_count: "",
        services: []
      }} validationRules={{
        name: {
          required: true,
          minLength: 2,
          maxLength: 50
        },
        email: {
          required: true,
          pattern: "email"
        },
        phone: {
          required: true,
          pattern: "phone"
        },
        event_date: {
          required: true
        },
        guest_count: {
          required: true,
          min: 1
        },
        services: {
          required: true
        }
      }} submitText="Get Your Quote" successMessage="Thank you! We'll review your details and send you a personalized quote within 24 hours." errorMessage="Something went wrong. Please try again or call us at (303) 555-1234.">
          <QuoteForm isVisible={isVisible} />
        </FormProvider>
      </div>
    </section>;
}
function QuoteForm({
  isVisible
}: {
  isVisible: boolean;
}) {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-8 bg-[hsl(190,80%,95%)] border-2 border-[hsl(190,80%,50%)] rounded-lg text-center">
        <div className="mb-4">
          <svg className="mx-auto size-16 text-[hsl(190,80%,50%)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <p className="text-foreground text-lg font-semibold mb-6">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="hover-lift">
          Submit Another Request
        </Button>
      </div>;
  }
  const serviceOptions = ["DJ", "Lighting", "Audio System", "Video Production", "Master of Ceremonies (MC)"];
  return <Form onSubmit={handleSubmit}>
      <div className="space-y-6">
        <div className={`transition-all duration-[var(--duration-medium)] ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
        transitionDelay: "200ms"
      }}>
          <FormField control={control} name="name" rules={{
          required: "Name is required",
          minLength: {
            value: 2,
            message: "Name must be at least 2 characters"
          },
          maxLength: {
            value: 50,
            message: "Name must be less than 50 characters"
          }
        }} render={({
          field
        }) => <FormItem>
                <FormLabel>Your Name</FormLabel>
                <FormDescription>First and last name</FormDescription>
                <FormControl>
                  <Input placeholder="Jane Doe" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />
        </div>

        <div className={`transition-all duration-[var(--duration-medium)] ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
        transitionDelay: "300ms"
      }}>
          <FormField control={control} name="email" rules={{
          required: "Email is required",
          pattern: {
            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
            message: "Please enter a valid email address"
          }
        }} render={({
          field
        }) => <FormItem>
                <FormLabel>Email Address</FormLabel>
                <FormControl>
                  <Input type="email" placeholder="jane@example.com" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />
        </div>

        <div className={`transition-all duration-[var(--duration-medium)] ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
        transitionDelay: "400ms"
      }}>
          <FormField control={control} name="phone" rules={{
          required: "Phone number is required",
          pattern: {
            value: /^[\d\s()+-]+$/,
            message: "Please enter a valid phone number"
          }
        }} render={({
          field
        }) => <FormItem>
                <FormLabel>Phone Number</FormLabel>
                <FormControl>
                  <Input type="tel" placeholder="(303) 555-1234" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />
        </div>

        <div className={`transition-all duration-[var(--duration-medium)] ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
        transitionDelay: "500ms"
      }}>
          <FormField control={control} name="event_date" rules={{
          required: "Event date is required"
        }} render={({
          field
        }) => <FormItem>
                <FormLabel>Event Date</FormLabel>
                <FormDescription>Select the date of your wedding or event</FormDescription>
                <FormControl>
                  <Input type="date" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />
        </div>

        <div className={`transition-all duration-[var(--duration-medium)] ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
        transitionDelay: "600ms"
      }}>
          <FormField control={control} name="guest_count" rules={{
          required: "Number of guests is required",
          min: {
            value: 1,
            message: "Please enter at least 1 guest"
          }
        }} render={({
          field
        }) => <FormItem>
                <FormLabel>Number of Guests</FormLabel>
                <FormControl>
                  <Input type="number" placeholder="150" min="1" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />
        </div>

        <div className={`transition-all duration-[var(--duration-medium)] ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
        transitionDelay: "700ms"
      }}>
          <FormField control={control} name="services" rules={{
          required: "Please select at least one service"
        }} render={({
          field
        }) => <FormItem>
                <FormLabel>Requested Services</FormLabel>
                <FormDescription>Select all services you are interested in</FormDescription>
                <div className="space-y-3 mt-3">
                  {serviceOptions.map((service, i) => <div key={service} data-index={i} className="flex items-center gap-3">
                      <Checkbox checked={field.value?.includes(service)} onCheckedChange={checked => {
                const current = field.value || [];
                const updated = checked ? [...current, service] : current.filter((s: string) => s !== service);
                field.onChange(updated);
              }} />
                      <label className="text-sm font-medium text-foreground cursor-pointer select-none">
                        {service}
                      </label>
                    </div>)}
                </div>
                <FormMessage />
              </FormItem>} />
        </div>

        {submitError && <div className="p-4 bg-[hsl(0,70%,95%)] border-2 border-[hsl(0,70%,50%)] rounded-lg">
            <p className="text-[hsl(0,70%,40%)] text-sm font-medium">{submitError}</p>
          </div>}

        <div className={`transition-all duration-[var(--duration-medium)] ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
        transitionDelay: "800ms"
      }}>
          <Button type="submit" disabled={isSubmitting} className="w-full btn-primary relative overflow-hidden">
            {isSubmitting ? <>
                <Loader className="animate-spin mr-2 size-4" />
                Sending Your Request...
              </> : "Get Your Quote"}
          </Button>
        </div>
      </div>
    </Form>;
}